import { MapMarker, Info } from '@withjoy/joykit/icons';
import React, { useCallback } from 'react';
import { ScheduleSection } from '../ScheduleSection';
import type { ScheduleEditorData } from '@shared/components/ScheduleEditor/ScheduleEditor.types';
import { CheckboxV2, DateSelection, Flex, FormControl, TextV2, TooltipV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core/i18n';
import { LocationInputV2 } from '@shared/components/LocationInputV2';
import type { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { GoogleMapEmbed } from '@shared/components/GoogleMapEmbed';

interface LocationSectionProps
  extends Readonly<{
    state: ScheduleEditorData;
    setValue: (key: keyof ScheduleEditorData, value: string | DateSelection | boolean | null) => void;
  }> {}

export const LocationSection = (props: LocationSectionProps) => {
  const { state, setValue } = props;
  const { tq } = useTranslation('scheduleEditor');

  const handleLocationChange = useCallback(
    (location: LocationInputV2Response | null) => {
      if (location) {
        const locationString = location.formattedAddress || location.displayName;
        setValue('location', locationString);

        // Only set placeId for valid Google Places, not custom text entries
        // Custom entries have placeId === displayName (the raw input string)
        const isValidPlace = location.placeId !== location.displayName;
        setValue('placeId', isValidPlace ? location.placeId : null);
      } else {
        setValue('location', null);
        setValue('placeId', null);
        setValue('showMap', false);
      }
    },
    [setValue],
  );

  const handleShowMapChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue('showMap', e.target.checked);
    },
    [setValue],
  );

  return (
    <ScheduleSection
      icon={<MapMarker size="md" />}
      title={tq`Location`}
    >
      <Flex
        gap={5}
        flexDirection="column"
      >
        <FormControl label={tq`Venue name or Address`}>
          <LocationInputV2
            name="location"
            placeholder={tq`Search for a location`}
            placePredictionTypes={['establishment', 'geocode']}
            onLocationChange={handleLocationChange}
            defaultValue={state.location || undefined}
          />
        </FormControl>
        <Flex
          alignItems="center"
          gap={2}
        >
          <CheckboxV2
            isChecked={state.showMap}
            onChange={handleShowMapChange}
            isDisabled={!state.location}
          >
            {tq`Show map of location on schedule page`}
          </CheckboxV2>
          <TooltipV2 content={<TextV2 typographyVariant="body2">{tq`Maps require a venue with an address`}</TextV2>}>
            <Info
              size="sm"
              color="mono8"
            />
          </TooltipV2>
        </Flex>
        {state.showMap && state.location && (
          <GoogleMapEmbed
            address={state.location}
            place={state.placeId ?? undefined}
            height="320px"
            a11yTitle={tq`Map for ${state.location}`}
          />
        )}
      </Flex>
    </ScheduleSection>
  );
};
